import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthFunnelDefinition, GrowthFunnelSnapshot, GrowthIntelligenceService, parseJsonRows } from './growth-intelligence.service';
import { combineUtcDateAndTime, defaultCalendarFromUtc, defaultCalendarToUtc } from './growth-datetime.util';

interface StageRow {
  key: string;
  name: string;
  order: number;
}

interface DropOffRow {
  fromStage?: string;
  toStage?: string;
  dropOffRate?: number;
  lostCount?: number;
}

@Component({
  standalone: true,
  selector: 'ain-growth-funnels',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent, JalaliDateInputComponent,
    FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, InputNumberModule, DialogModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.intelligence.funnels.title')" [description]="t('growth.intelligence.funnels.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.intelligence.funnels.help.title'" [descriptionKey]="'growth.intelligence.funnels.help.description'"
        [bulletKeys]="['growth.intelligence.funnels.help.bullets.stages', 'growth.intelligence.funnels.help.bullets.snapshot']" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.intelligence.funnels.actions.newFunnel')" (click)="openFunnelDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (funnels().length === 0) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
        } @else {
          <p-table [value]="funnels()" [rows]="8" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.intelligence.funnels.columns.name') }}</th>
                <th>{{ t('growth.intelligence.funnels.columns.key') }}</th>
                <th>{{ t('growth.intelligence.funnels.columns.status') }}</th>
                <th>{{ t('growth.intelligence.funnels.columns.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.name }}</td>
                <td>{{ row.funnelKey }}</td>
                <td>{{ row.status }}</td>
                <td><button pButton type="button" size="small" [label]="t('common.select')" (click)="selectFunnel(row)"></button></td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <section class="card">
        <div class="section-title"><h3>{{ t('growth.intelligence.funnels.sections.refresh') }}</h3></div>
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.funnels.fields.periodStart')"><ain-date-input [(ngModel)]="refreshForm.periodStartUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.funnels.fields.periodEnd')"><ain-date-input [(ngModel)]="refreshForm.periodEndUtc" /></ain-form-field>
        </div>
        <button pButton type="button" [disabled]="!selectedFunnelId" [label]="t('growth.intelligence.funnels.actions.refreshSnapshot')" (click)="refreshSnapshot()"></button>
      </section>

      <section class="card">
        <div class="section-title"><h3>{{ t('growth.intelligence.funnels.sections.dropOff') }}</h3></div>
        @if (dropOffRows().length === 0) {
          <ain-state type="empty" [title]="t('growth.intelligence.funnels.emptyDropOff')" />
        } @else {
          <p-table [value]="dropOffRows()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.intelligence.funnels.columns.fromStage') }}</th>
                <th>{{ t('growth.intelligence.funnels.columns.toStage') }}</th>
                <th>{{ t('growth.intelligence.funnels.columns.dropOffRate') }}</th>
                <th>{{ t('growth.intelligence.funnels.columns.lostCount') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.fromStage ?? '-' }}</td>
                <td>{{ row.toStage ?? '-' }}</td>
                <td>{{ row.dropOffRate ?? 0 }}</td>
                <td>{{ row.lostCount ?? 0 }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <p-dialog
        [header]="t('growth.intelligence.funnels.actions.newFunnel')"
        [visible]="funnelDialogVisible()"
        (visibleChange)="funnelDialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(760px, 96vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.funnels.fields.name')"><input pInputText [(ngModel)]="funnelForm.name" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.funnels.fields.funnelKey')"><input pInputText [(ngModel)]="funnelForm.funnelKey" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.funnels.fields.status')">
            <p-select [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="funnelForm.status" appendTo="body" />
          </ain-form-field>
        </div>
        <div class="section-title"><h4>{{ t('growth.intelligence.funnels.sections.stages') }}</h4></div>
        @for (row of stageRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.intelligence.funnels.fields.stageKey')"><input pInputText [(ngModel)]="row.key" /></ain-form-field>
            <ain-form-field [label]="t('growth.intelligence.funnels.fields.stageName')"><input pInputText [(ngModel)]="row.name" /></ain-form-field>
            <ain-form-field [label]="t('growth.intelligence.funnels.fields.stageOrder')"><p-inputNumber [(ngModel)]="row.order" [min]="1" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeStage($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addStage()"></button>
        <ng-template pTemplate="footer">
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="funnelDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveFunnel()"></button>
          </div>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .row-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); align-items: end; margin-bottom: 8px; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthFunnelsComponent implements OnInit {
  private readonly service = inject(GrowthIntelligenceService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly funnelDialogVisible = signal(false);
  readonly funnels = signal<GrowthFunnelDefinition[]>([]);
  readonly dropOffRows = signal<DropOffRow[]>([]);

  selectedFunnelId: string | null = null;
  funnelForm = { name: '', funnelKey: '', status: 'Active' };
  stageRows: StageRow[] = [
    { key: 'awareness', name: 'Awareness', order: 1 },
    { key: 'acquisition', name: 'Acquisition', order: 2 }
  ];
  refreshForm = { periodStartUtc: defaultCalendarFromUtc(), periodEndUtc: defaultCalendarToUtc() };
  readonly statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Archived', label: 'Archived' }
  ];

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.service.listFunnels().subscribe({
      next: items => { this.funnels.set(items); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }

  openFunnelDialog(): void {
    this.funnelForm = { name: '', funnelKey: '', status: 'Active' };
    this.stageRows = [{ key: 'awareness', name: 'Awareness', order: 1 }];
    this.funnelDialogVisible.set(true);
  }

  saveFunnel(): void {
    this.service.upsertFunnel({
      funnelKey: this.funnelForm.funnelKey || null,
      name: this.funnelForm.name,
      stageDefinitionsJson: JSON.stringify(this.stageRows),
      status: this.funnelForm.status
    }).subscribe({
      next: item => {
        this.selectedFunnelId = item.id;
        this.funnelDialogVisible.set(false);
        this.load();
      }
    });
  }

  selectFunnel(row: GrowthFunnelDefinition): void {
    this.selectedFunnelId = row.id;
    this.funnelForm = { name: row.name, funnelKey: row.funnelKey, status: row.status };
    this.stageRows = parseJsonRows<StageRow>(row.stageDefinitionsJson, this.stageRows);
    this.service.listFunnelSnapshots(row.id).subscribe({
      next: snapshots => {
        const latest = snapshots[0];
        this.dropOffRows.set(latest ? parseJsonRows<DropOffRow>(latest.dropOffJson) : []);
      }
    });
  }

  refreshSnapshot(): void {
    if (!this.selectedFunnelId) return;
    this.service.refreshFunnel(this.selectedFunnelId, {
      periodStartUtc: combineUtcDateAndTime(this.refreshForm.periodStartUtc, '00:00')!,
      periodEndUtc: combineUtcDateAndTime(this.refreshForm.periodEndUtc, '23:59')!
    }).subscribe({
      next: snapshot => this.dropOffRows.set(parseJsonRows<DropOffRow>(snapshot.dropOffJson))
    });
  }

  addStage(): void { this.stageRows.push({ key: '', name: '', order: this.stageRows.length + 1 }); }
  removeStage(index: number): void { this.stageRows.splice(index, 1); }
  t(key: string): string { return this.i18n.translate(key); }
}
